import {defineStore} from "pinia";
import {reactive, ref} from "vue";
import {serializeFormData} from "../helpers/CaseTransformer.js";
import {parsePartialCandidateData} from "../helpers/HandleStreamedJson.js";
import {computeCandidateTrustScore} from "../helpers/TrustScore.js";
import {usaCities} from "../constants/Lists.js";
import {useCandidateProfileStore} from "./CandidateProfileStore.js";
import {useAccountStore} from "./AccountStore.js";
import ParseResumeApi from "../api/ParseResumeApi.js";
import * as Sentry from "@sentry/vue";
import {useLocalStorage} from "@vueuse/core";
import {cloudfrontUrlToFile} from "../helpers/CloudfrontHelpers";

export const useParseResumeStore = defineStore("resumeParser", () => {
  const defaultResume = {
    uuid: null,
    file: null,
    fileMimeType: null,
    fileDataUrl: null,
    tosChecked: false,
    parsedContent: "",
    name: "",
    formattedSize: "",
    uploading: false,
    streaming: false,
    confirm: false,
    error: null,
    thinking: false,
    loading: false,
  };

  const defaultCandidate = {
    trustScore: 53,
    avatar: "",
    banner: "",
    bannerFile: {
      path: "",
      file: null,
    },
    profilePhoto: {
      path: "",
      file: null,
    },
    about: "",
    summary: {
      prompt: "",
      answer: "",
    },
    location: "",
    lat: null,
    lng: null,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    willingToRelocate: "Open to Relocation",
    remote: "",
    interestedIn: [],
    industries: [],
    fullTime: ["Full Time"],
    availability: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    desiredHourlyPay: 15,
    compensationFlexibility: 0.2,
    prefersHourlyOverSalary: true,
    exceptionalAbility: [],
    profileVideo1: {
      path: "",
      prompt: "",
      file: null,
    },
    profileVideo2: {
      path: "",
      prompt: "",
      file: null,
    },
    skills: [],
    accomplishments: [],
    careerPassport: [],
    education: [],
    certificatesCertificationsAndLicenses: [],
    languages: ["English"],
    studentAthlete: false,
    hobbies: "",
    military: false,
    linkedIn: "",
    instagram: "",
    facebook: "",
    website: "",
  };

  const defaultParsedSearchTerms = {
    industries: [],
    jobs: [],
  };

  const resume = reactive(Object.assign({showModal: false}, defaultResume));
  const candidate = reactive(Object.assign({}, defaultCandidate));
  const parsedSearchTerms = reactive(Object.assign({}, defaultParsedSearchTerms));
  const localResume = useLocalStorage("localResume", {});

  const reset = () => {
    for (const callback of resetCallbacks.value) {
      if (callback() === true) return;
    }

    Object.assign(resume, defaultResume);
    Object.assign(candidate, defaultCandidate);
    Object.assign(parsedSearchTerms, defaultParsedSearchTerms);
    Object.assign(localResume.value, {});
  };

  /**
   * Uploads the selected resume and stores the identifier it was given.
   *
   * The uploading flag is released even when the upload fails, so a candidate
   * can retry without reloading the page.
   *
   * @param  {ParseResumeApi} api
   * @return {Promise<void>}
   */
  const uploadResume = async (api) => {
    if (resume.uploading) return;
    resume.uploading = true;

    try {
      const formData = serializeFormData({}, "post");
      formData.append("resume", resume.file);

      const response = await api.storeResume(formData);

      resume.uuid = response.uuid;

      if (!localResume.value) localResume.value = {};
      Object.assign(localResume.value, {
        uuid:          response.uuid,
        name:          resume.name,
        formattedSize: resume.formattedSize,
        used:          new Date(),
      });
    } finally {
      resume.uploading = false;
    }
  };

  const setTrustScore = () => {
    let scores = computeCandidateTrustScore(candidate);
    candidate.trustScore = scores.overall;
  };

  const resetToInitialCityState = (initialCity, initialState) => {
    candidate.city = initialCity;
    candidate.state = initialState;
  };

  const pinLatLng = async (initialCity, initialState) => {
    if (!candidate.city || !candidate.state) {
      resetToInitialCityState(initialCity, initialState);
      return;
    }

    try {
      const searchResults = await usaCities({
        search: `${candidate.city} ${candidate.state}`,
        page: 1,
        perpage: 1,
      });

      if (searchResults?.data?.length) {
        const result = searchResults.data[0];

        candidate.city = result.city;
        candidate.state = result.state;
        candidate.lat = result.lat;
        candidate.lng = result.lng;
      } else resetToInitialCityState(initialCity, initialState);

    } catch (e) {
      console.error(e);
      resetToInitialCityState(initialCity, initialState);
    }
  };

  const setIndustriesAndInterestedIn = async (api) => {
    try {
      const response = await api.searchIndustriesAndJobTitles(parsedSearchTerms.jobs, parsedSearchTerms.industries);
      candidate.interestedIn = response.job_titles || [];
      candidate.industries = response.industries || [];
      const basicInfo = document.getElementById("candidateBasicInfo");
      if (basicInfo) basicInfo.scrollIntoView({behavior: "smooth"});
    } catch (e) {
      console.error(e);
    }
  };

  let thinkingTimout;

  const clearThinkingTimeout = () => {
    resume.thinking = false;
    clearTimeout(thinkingTimout);
  };

  const resetThinkingTimeout = () => {
    clearThinkingTimeout();
    thinkingTimout = setTimeout(() => {
      resume.thinking = !!resume.streaming;
    }, 500);
  };

  const parseResumeContent = async (api) => {

    const eventSource = api.parseResumeContent(
      resume.uuid,
    );

    try {
      resume.parsedContent = "";
      resume.streaming = true;

      let handledAtLeastOneEvent = false;
      const initialCity = candidate.city;
      const initialState = candidate.state;

      window.addEventListener("beforeunload", () => eventSource?.close());

      eventSource.addEventListener("update", async function (event) {
        if (!handledAtLeastOneEvent) handledAtLeastOneEvent = true;

        if (event.data === "<END_STREAMING_SSE>") {
          eventSource.close();
          await pinLatLng(initialCity, initialState);
          await setIndustriesAndInterestedIn(api);
          resume.streaming = false;
          setTrustScore();
          const top = document.getElementById("candidateProfileContainer");
          if (top) top.scrollIntoView({behavior: "smooth", block: "start"});
          return;
        }

        if (!event.data) return;

        resume.parsedContent += event.data;
        let result;

        try {
          result = parsePartialCandidateData(resume.parsedContent);
          if (result?.searchTerms?.industries) parsedSearchTerms.industries = result.searchTerms.industries;
          if (result?.searchTerms?.jobs) parsedSearchTerms.jobs = result.searchTerms.jobs;
          resetThinkingTimeout();
        } catch (e) {
          console.error(e);
          resume.error = e.message;
          Sentry.captureException(e);
          clearThinkingTimeout();
        }

        if (!result?.data) return;
        Object.assign(candidate, result.data);
      });

      const handleError = (event, type) => {
        eventSource.close();
        resume.streaming = false;

        let errorMsg;
        let toastMsg;

        if (!handledAtLeastOneEvent) {
          errorMsg = "Hmm, it looks like we weren't able to read any data from that resume. Please check that it's not empty, and try again. If the problem persists, please contact support@businessdraft.com";
          toastMsg = "The resume provided is empty. Please upload a resume with content.";
        } else if (type === "transporter-error") {
          errorMsg = "Our resume parsing service is down. Please try again later.";
          toastMsg = "Uh oh! Our third-party resume parsing service is down. Please try again later.";
        } else if (type === "configuration-error") {
          errorMsg = "Our resume parsing service is misconfigured - please contact support.";
          toastMsg = "Our resume parsing service is misconfigured. Please contact support.";
        } else if (type === "unserializable-error") {
          errorMsg = "Our resume parsing service is having trouble reading your resume. Please contact support.";
          toastMsg = " Our resume parsing service is having trouble reading your resume. Please contact support.";
        } else {
          errorMsg = "Lost connection to the server. Please try again.";
          toastMsg = "An error occurred while parsing your resume";
        }

        toast.error(toastMsg);
        resume.error = errorMsg;
      };

      eventSource.addEventListener("transporter-error", (event) => handleError(event, "transporter-error"));
      eventSource.addEventListener("configuration-error", (event) => handleError(event, "configuration-error"));
      eventSource.addEventListener("unserializable-error", (event) => handleError(event, "unserializable-error"));
      eventSource.addEventListener("error", (event) => handleError(event, "error"));

    } catch (e) {
      console.error(e);
      resume.streaming = false;
      clearThinkingTimeout();
      throw new Error("Either 1) The resume was too large (> 6 pages) 2) Our robot detector went off, or 3) The server connection timed-out.");
    }
  };

  const parseResume = async (showTermsOfService = true) => {
    if (resume.loading) return;

    const accountStore = useAccountStore();

    if (showTermsOfService && !resume.tosChecked && !accountStore.isLoggedIn) {
      toast.warning("Please accept the terms of service");
      return;
    }

    if (!resume.file) {
      resume.error = "Please select a resume to upload";
      return;
    }

    const api = new ParseResumeApi();

    resume.loading = true;
    resume.error = null;
    resume.showModal = true;

    try {
      if (!resume.uuid || resume.uuid !== localResume.value?.uuid) await uploadResume(api);

      if (accountStore.isLoggedIn) {
        const candidateProfileStore = useCandidateProfileStore();
        Object.assign(candidate, candidateProfileStore.state);
      }

      resume.confirm = true;
      resume.streaming = true;

      await parseResumeContent(api);

      resume.streaming = true;
      resume.confirm = true;
    } catch (e) {
      console.error(e);
      resume.error = e?.message || "An unknown error occurred while parsing your resume";
    } finally {
      resume.uploading = false;
    }
  };

  /**
   * Parses the contact details out of the uploaded resume.
   *
   * The upload has to have produced an identifier first, otherwise the request
   * is skipped rather than sent with a null uuid for the server to reject.
   *
   * @return {Promise<Object|undefined>}
   */
  const parseContactInfo = async () => {
    if (resume.loading) return;

    const api = new ParseResumeApi();

    if (!resume.uuid || resume.uuid !== localResume.value?.uuid) await uploadResume(api);

    if (!resume.uuid) throw new Error("We couldn't upload your resume. Please try again.");

    return await api.parseResumeContactInfo(resume.uuid);
  };

  const attemptToLoadOldResume = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const uuidParam = urlParams.get("uuid");
    const usedParam = urlParams.get("used");

    const uuid = localResume.value?.uuid ? localResume.value?.uuid : uuidParam;
    const usedAt = localResume.value?.used ? localResume.value?.used : usedParam;

    if (!uuid || !usedAt) {
      localResume.value = {};
      return;
    }

    if (!localResume.value?.uuid) localResume.value = {uuid, used: usedAt};

    const validForDays = 7;

    if (new Date(usedAt) < new Date(Date.now() - validForDays * 24 * 60 * 60 * 1000)) {
      localResume.value = {};
      return;
    }

    const api = new ParseResumeApi();

    try {
      const response = await api.show(uuid);

      const path = response.path;
      if (!path) {
        localResume.value = {};
        return;
      }

      await cloudfrontUrlToFile(path, (error, result) => {
        if (error) {
          console.error(error);
          return;
        }

        const name = localResume.value?.name ? localResume.value?.name : "Resume Uploaded";
        const formattedSize = localResume.value?.formattedSize ? localResume.value?.formattedSize : "On another device";

        resume.uuid = uuid;
        resume.file = result.file;
        resume.fileDataUrl = result.dataUrl;
        resume.name = name;
        resume.formattedSize = formattedSize;
      });

    } catch (e) {
      console.error(e);
    }
  };

  const completeCallbacks = ref([]);
  const resetCallbacks = ref([]);

  const addCompletionCallback = (callback) => {
    if (typeof callback !== "function") throw new Error("The callback must be a function");
    completeCallbacks.value.push(callback);
  };

  const addResetCallback = (callback) => {
    if (typeof callback !== "function") throw new Error("The callback must be a function");
    resetCallbacks.value.push(callback);
  };

  const flushCompletionCallbacks = () => {
    completeCallbacks.value = [];
    resetCallbacks.value = [];
  };

  const complete = () => {
    completeCallbacks.value.forEach((callback) => callback());
    flushCompletionCallbacks();
  };

  const $reset = () => {
    reset();
  };


  return {
    resume,
    candidate,
    localResume,
    setTrustScore,
    parseResume,
    reset,
    addResetCallback,
    addCompletionCallback,
    flushCompletionCallbacks,
    complete,
    parseContactInfo,
    attemptToLoadOldResume,
    $reset
  };
});
